import React, { useState, useEffect, useRef } from 'react';
import SpeechBubble from './SpeechBubble';
import { getHelperMessage } from './csvParser';
import './CatAnimation.css';

const CatAnimation = ({ selectedTeam, theme = 'light' }) => {
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [state, setState] = useState('sitting'); // sitting, grooming, running, sleeping
  const [isHovered, setIsHovered] = useState(false);
  const [groomingProgress, setGroomingProgress] = useState(0);
  const [sleepingProgress, setSleepingProgress] = useState(0);
  const [helperMessage, setHelperMessage] = useState('');
  const [showBubble, setShowBubble] = useState(false);
  const [isShowingMessage, setIsShowingMessage] = useState(false);
  // НОВЫЙ КОД - состояние для кнопки помощи (ЗАКОММЕНТИРОВАТЬ ПРИ ОТКАТЕ)
  const [showHelpButton, setShowHelpButton] = useState(true);
  // КОНЕЦ НОВОГО КОДА - состояние для кнопки помощи
  
  const sleepTimeoutRef = useRef(null);
  const groomingTimeoutRef = useRef(null);
  const stateTimeoutRef = useRef(null);
  const groomingProgressIntervalRef = useRef(null);
  const sleepingProgressIntervalRef = useRef(null);

  // Генерация случайной позиции
  const generateRandomPosition = () => {
    const maxX = window.innerWidth - 100;
    const maxY = window.innerHeight - 100;
    return {
      x: Math.random() * maxX,
      y: Math.random() * maxY
    };
  };

  // Изменение состояния кошки
  const changeState = (newState) => {
    setState(newState);
    
    // Очистка предыдущих таймеров
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
    }
    
    // Установка следующего состояния
    const stateDurations = {
      sitting: 4000,
      grooming: 3000,
      sleeping: 5000,
      running: 2000
    };
    
    stateTimeoutRef.current = setTimeout(() => {
      const states = ['sitting', 'grooming', 'sleeping'];
      const randomState = states[Math.floor(Math.random() * states.length)];
      changeState(randomState);
    }, stateDurations[newState] || 3000);
  };

  // Обработка наведения мыши на кошку
  const handleMouseEnter = () => {
    // Если кошка показывает сообщение, она не убегает
    if (isShowingMessage) {
      return;
    }
    
    setIsHovered(true);
    setState('running');
    
    // Очистка таймеров
    if (sleepTimeoutRef.current) {
      clearTimeout(sleepTimeoutRef.current);
    }
    if (groomingTimeoutRef.current) {
      clearTimeout(groomingTimeoutRef.current);
    }
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
    }
    
    // Перемещение кошки
    const newPosition = generateRandomPosition();
    setPosition(newPosition);
    
    // Возврат к обычному состоянию через 2 секунды
    setTimeout(() => {
      setIsHovered(false);
      changeState('sitting');
    }, 2000);
  };

  // Обработка клика
  const handleClick = () => {
    if (state === 'sleeping') {
      setState('sitting');
      setSleepingProgress(0);
    } else {
      changeState('grooming');
    }
  };

  // Обработка наведения на пузырьковое сообщение
  const handleBubbleMouseEnter = () => {
    setIsShowingMessage(true);
  };

  // Обработка ухода мыши с пузырькового сообщения
  const handleBubbleMouseLeave = () => {
    // Небольшая задержка, чтобы пользователь успел кликнуть по ссылке
    setTimeout(() => {
      setIsShowingMessage(false);
    }, 500);
  };

  // Обработка движения мыши по кошке (только когда не показывает сообщение)
  const handleMouseMove = (e) => {
    if (isShowingMessage) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
  };

  // Инициализация
  useEffect(() => {
    setPosition(generateRandomPosition());
    changeState('sitting');
    
    // Автоматическое изменение позиции каждые 30 секунд
    const positionInterval = setInterval(() => {
      setPosition(prevPosition => {
        if (!isHovered && state !== 'running') {
          return generateRandomPosition();
        }
        return prevPosition;
      });
    }, 30000);
    
    return () => {
      clearInterval(positionInterval);
      if (stateTimeoutRef.current) {
        clearTimeout(stateTimeoutRef.current);
      }
    };
  }, [isHovered, state]);

  // Обработка изменения выбранной команды
  useEffect(() => {
    if (selectedTeam) {
      // Извлекаем номер команды из названия (например, "Команда 1" -> "1")
      const teamNumber = selectedTeam.match(/\d+/);
      if (teamNumber) {
        const message = getHelperMessage(teamNumber[0]);
        setHelperMessage(message);
        setShowBubble(true);
        setIsShowingMessage(true); // Кошка показывает сообщение
        // НОВЫЙ КОД - сброс состояния кнопки помощи при смене команды (ЗАКОММЕНТИРОВАТЬ ПРИ ОТКАТЕ)
        setShowHelpButton(true);
        // КОНЕЦ НОВОГО КОДА - сброс состояния кнопки помощи при смене команды
      } else {
        setHelperMessage('');
        setShowBubble(false);
        setIsShowingMessage(false);
      }
    } else {
      setHelperMessage('');
      setShowBubble(false);
      setIsShowingMessage(false);
    }
  }, [selectedTeam]);

  // Обработка прогресса вылизывания
  useEffect(() => {
    if (state === 'grooming') {
      setGroomingProgress(0);
      groomingProgressIntervalRef.current = setInterval(() => {
        setGroomingProgress(prev => {
          if (prev >= 100) {
            clearInterval(groomingProgressIntervalRef.current);
            return 100;
          }
          return prev + 2;
        });
      }, 60);
    } else {
      setGroomingProgress(0);
      if (groomingProgressIntervalRef.current) {
        clearInterval(groomingProgressIntervalRef.current);
        groomingProgressIntervalRef.current = null;
      }
    }
    
    return () => {
      if (groomingProgressIntervalRef.current) {
        clearInterval(groomingProgressIntervalRef.current);
        groomingProgressIntervalRef.current = null;
      }
    };
  }, [state]);

  // Обработка прогресса сна
  useEffect(() => {
    if (state === 'sleeping') {
      setSleepingProgress(0);
      sleepingProgressIntervalRef.current = setInterval(() => {
        setSleepingProgress(prev => {
          if (prev >= 100) {
            clearInterval(sleepingProgressIntervalRef.current);
            return 100;
          }
          return prev + 1;
        });
      }, 50);
    } else {
      setSleepingProgress(0);
      if (sleepingProgressIntervalRef.current) {
        clearInterval(sleepingProgressIntervalRef.current);
        sleepingProgressIntervalRef.current = null;
      }
    }
    
    return () => {
      if (sleepingProgressIntervalRef.current) {
        clearInterval(sleepingProgressIntervalRef.current);
        sleepingProgressIntervalRef.current = null;
      }
    };
  }, [state]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (sleepTimeoutRef.current) {
        clearTimeout(sleepTimeoutRef.current);
      }
      if (groomingTimeoutRef.current) {
        clearTimeout(groomingTimeoutRef.current);
      }
      if (stateTimeoutRef.current) {
        clearTimeout(stateTimeoutRef.current);
      }
      if (groomingProgressIntervalRef.current) {
        clearInterval(groomingProgressIntervalRef.current);
        groomingProgressIntervalRef.current = null;
      }
      if (sleepingProgressIntervalRef.current) {
        clearInterval(sleepingProgressIntervalRef.current);
        sleepingProgressIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <div className="cat-container">
      <div
        className={`cat cat--${state} ${isShowingMessage ? 'cat--showing-message' : ''}`}
        style={{
          left: position.x,
          top: position.y,
          transform: isHovered ? 'scale(1.1)' : 'scale(1)'
        }}
        onMouseEnter={isShowingMessage ? null : handleMouseEnter}
        onClick={isShowingMessage ? null : handleClick}
        onMouseMove={handleMouseMove}
      >

        {/* Прогресс-бар для вылизывания */}
        {state === 'grooming' && (
          <div className="cat-progress">
            <div 
              className="cat-progress-bar cat-progress-bar--grooming"
              style={{ width: `${groomingProgress}%` }}
            />
          </div>
        )}
        
        {/* Прогресс-бар для сна */}
        {state === 'sleeping' && (
          <div className="cat-progress">
            <div 
              className="cat-progress-bar cat-progress-bar--sleeping"
              style={{ width: `${sleepingProgress}%` }}
            />
          </div>
        )}
        
        {/* ZZZ для сна */}
        {state === 'sleeping' && (
          <div className="cat-sleeping">
            <span className="cat-zzz">Z</span>
            <span className="cat-zzz cat-zzz--2">Z</span>
            <span className="cat-zzz cat-zzz--3">Z</span>
          </div>
        )}
        
        {/* Тело кошки */}
        <div className="cat-body">
          {/* Голова */}
          <div className="cat-head">
            {/* Уши */}
            <div className="cat-ears">
              <div className="cat-ear cat-ear--left"></div>
              <div className="cat-ear cat-ear--right"></div>
            </div>
            
            {/* Лицо */}
            <div className="cat-face">
              {/* Глаза */}
              <div className="cat-eyes">
                <div className="cat-eye cat-eye--left"></div>
                <div className="cat-eye cat-eye--right"></div>
              </div>
              
              {/* Нос */}
              <div className="cat-nose"></div>
              
              {/* Рот */}
              <div className="cat-mouth"></div>
            </div>
            
            {/* Усы */}
            <div className="cat-whiskers">
              <div className="cat-whisker cat-whisker--left-1"></div>
              <div className="cat-whisker cat-whisker--left-2"></div>
              <div className="cat-whisker cat-whisker--left-3"></div>
              <div className="cat-whisker cat-whisker--right-1"></div>
              <div className="cat-whisker cat-whisker--right-2"></div>
              <div className="cat-whisker cat-whisker--right-3"></div>
            </div>
          </div>
          
          {/* Хвост */}
          <div className="cat-tail">
            <div className="cat-tail-tip"></div>
          </div>
          
          {/* Лапы */}
          <div className="cat-paws">
            <div className="cat-paw cat-paw--front-left"></div>
            <div className="cat-paw cat-paw--front-right"></div>
            <div className="cat-paw cat-paw--back-left"></div>
            <div className="cat-paw cat-paw--back-right"></div>
          </div>
          
          {/* Элементы для вылизывания */}
          {state === 'grooming' && (
            <>
              <div className="cat-tongue"></div>
              <div className="cat-paw-grooming"></div>
            </>
          )}
        </div>

        {/* Пузырьковое сообщение (привязано к кошке) */}
        <SpeechBubble
          message={helperMessage}
          isVisible={showBubble}
          onMouseEnter={handleBubbleMouseEnter}
          onMouseLeave={handleBubbleMouseLeave}
          catPosition={position}
          // НОВЫЙ КОД - передаем состояние кнопки помощи (ЗАКОММЕНТИРОВАТЬ ПРИ ОТКАТЕ)
          showHelpButton={showHelpButton}
          setShowHelpButton={setShowHelpButton}
          // КОНЕЦ НОВОГО КОДА - передаем состояние кнопки помощи
        />
      </div>
      
    </div>
  );
};

export default CatAnimation; 